import { useEffect, useMemo, useState } from "react";
import {
  Plane, Send, Search, RefreshCw, Download, FileText, Users2, MapPin, Calendar, Check, X,
  FileCheck, Receipt, AlertCircle, ChevronRight, Wallet, ClipboardList, BellRing, PenLine, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage, formatTanggalID, getToken } from "@/lib/api";
import { PlaceAutocomplete } from "@/components/PlaceAutocomplete";

const LEADER_ROLES = new Set(["admin", "manager", "asisten_manager", "general_manager", "kepala_divisi", "direksi"]);

const STATUS_CLASS = {
  "Menunggu Approval Atasan": "text-amber-700 bg-amber-50 border-amber-200",
  "Menunggu Approval Penandatangan ST": "text-indigo-700 bg-indigo-50 border-indigo-200",
  "Surat Tugas Terbit": "text-emerald-700 bg-emerald-50 border-emerald-200",
  "Ditolak Atasan": "text-rose-700 bg-rose-50 border-rose-200",
  "Ditolak Penandatangan": "text-rose-700 bg-rose-50 border-rose-200",
  // Legacy — hanya muncul utk dokumen lama sebelum perubahan alur Feb 2026.
  "Menunggu Approval Pegawai": "text-amber-700 bg-amber-50 border-amber-200",
  "Ditolak Pegawai": "text-rose-700 bg-rose-50 border-rose-200",
};

const SECTION8_SIGNERS = [
  { key: "pelaksana",      label: "PELAKSANA" },
  { key: "manager",        label: "MANAGER UNIT" },
  { key: "kadiv_keu",      label: "KADIV KEU & ADM" },
  { key: "direktur",       label: "DIREKTUR" },
  { key: "direktur_utama", label: "DIREKTUR UTAMA" },
];

function StatusBadge({ status }) {
  if (!status) return null;
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold rounded-full border px-2 py-0.5 ${STATUS_CLASS[status] || "text-slate-700 bg-slate-50 border-slate-200"}`} data-testid="sppd-status-badge">
      {status}
    </span>
  );
}

function rp(v) {
  const n = Number(v || 0);
  return "Rp " + n.toLocaleString("id-ID");
}

function PerluTindakanBadges({ items, testId }) {
  if (!items?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1" data-testid={testId}>
      {items.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 text-[10px] font-bold rounded-full bg-rose-600 text-white px-2 py-0.5 shadow-sm">
          <BellRing size={10} /> {t}
        </span>
      ))}
    </div>
  );
}

export default function PerintahSppd() {
  const { user } = useAuth();
  const [tab, setTab] = useState("semua");
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const isLeader = user && LEADER_ROLES.has(user.role);
  const isAdmin = user?.role === "admin";

  async function load() {
    setLoading(true);
    try {
      const [all, pend] = await Promise.all([
        api.get("/sppd/perintah"),
        api.get("/sppd/perintah/pending").catch(() => ({ data: [] })),
      ]);
      setRows(all.data || []);
      setPending(pend.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (user?.id) load(); /* eslint-disable-next-line */ }, [user?.id]);

  const mine = useMemo(() => rows.filter((r) => r.pegawai_user_id === user?.id), [rows, user?.id]);
  const diajukan = useMemo(() => rows.filter((r) => r.pengaju_user_id === user?.id), [rows, user?.id]);
  const terbit = useMemo(() => rows.filter((r) => r.status === "Surat Tugas Terbit"), [rows]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-perintah-sppd">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-sky-700 flex items-center gap-2">
            <Plane size={14} /> Perjalanan Dinas
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Surat Perintah Perjalanan Dinas (SPPD)
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Alur: atasan ajukan → penandatangan ST approve → ST &amp; SPPD terbit → uang muka → pertanggungjawaban (Section 6 &amp; 7) → TTD Section 8.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2" data-testid="sppd-refresh-btn">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          {isLeader && (
            <Button onClick={() => setCreateOpen(true)} className="gap-2 bg-sky-700 hover:bg-sky-800" data-testid="sppd-create-btn">
              <Send size={14} /> Ajukan SPPD
            </Button>
          )}
        </div>
      </div>

      {pending.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm p-3 flex items-start gap-2" data-testid="sppd-pending-banner">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <div>
            <b>{pending.length} dokumen</b> menunggu tindakan Anda.
            Lihat tab "Perlu Tindakan" di bawah.
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1" data-testid="sppd-tabs">
          <TabsTrigger value="semua" data-testid="sppd-tab-semua">Semua</TabsTrigger>
          {pending.length > 0 && <TabsTrigger value="pending" data-testid="sppd-tab-pending">Perlu Tindakan ({pending.length})</TabsTrigger>}
          <TabsTrigger value="mine" data-testid="sppd-tab-mine">Sebagai Pelaksana ({mine.length})</TabsTrigger>
          {isLeader && <TabsTrigger value="diajukan" data-testid="sppd-tab-diajukan">Saya Ajukan ({diajukan.length})</TabsTrigger>}
          {isAdmin && <TabsTrigger value="pembayaran" data-testid="sppd-tab-pembayaran">Pembayaran Pending</TabsTrigger>}
        </TabsList>
        <TabsContent value="semua" className="mt-4">
          <PerintahList data={rows} loading={loading} onOpen={setDetailId} />
        </TabsContent>
        <TabsContent value="pending" className="mt-4">
          <PerintahList data={pending} loading={loading} onOpen={setDetailId} />
        </TabsContent>
        <TabsContent value="mine" className="mt-4">
          <PerintahList data={mine} loading={loading} onOpen={setDetailId} />
        </TabsContent>
        <TabsContent value="diajukan" className="mt-4">
          <PerintahList data={diajukan} loading={loading} onOpen={setDetailId} />
        </TabsContent>
        <TabsContent value="pembayaran" className="mt-4">
          <PerintahList data={terbit.filter((r) => r.section4_status === "pending_admin" || r.section7_status === "pending_admin")} loading={loading} onOpen={setDetailId} highlightPembayaran />
        </TabsContent>
      </Tabs>

      {createOpen && (
        <CreateDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSuccess={() => { setCreateOpen(false); load(); }}
        />
      )}

      {detailId && (
        <DetailDialog
          perintahId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
          user={user}
        />
      )}
    </div>
  );
}

// ========================================================================
// LIST
// ========================================================================

function PerintahList({ data, loading, onOpen, highlightPembayaran }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nomor ST / SPPD</TableHead>
            <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pelaksana</TableHead>
            <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Tujuan</TableHead>
            <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Periode</TableHead>
            <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Status</TableHead>
            {highlightPembayaran && <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pembayaran</TableHead>}
            <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right w-32">Aksi</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={highlightPembayaran ? 7 : 6} className="text-center text-slate-400 py-10">Memuat…</TableCell></TableRow>
          ) : data.length === 0 ? (
            <TableRow><TableCell colSpan={highlightPembayaran ? 7 : 6} className="text-center text-slate-400 py-10">Belum ada data.</TableCell></TableRow>
          ) : data.map((r) => (
            <TableRow key={r.id} data-testid={`sppd-row-${r.id}`} className={r.perlu_tindakan?.length ? "bg-rose-50/60 hover:bg-rose-50" : ""}>
              <TableCell>
                <div className="font-mono-clock text-xs font-semibold text-slate-900">{r.nomor_st || "—"}</div>
                <div className="font-mono-clock text-[10px] text-slate-500">{r.nomor_sppd || ""}</div>
              </TableCell>
              <TableCell>
                <div className="font-semibold text-slate-900">{r.pegawai_nama}</div>
                <div className="text-[11px] text-slate-500">{r.pegawai_npp} · {r.pegawai_jabatan_nama}</div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-slate-900 font-medium">{r.nama_perusahaan}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1"><MapPin size={10} />{r.tempat_pelaksanaan}</div>
              </TableCell>
              <TableCell>
                <div className="text-xs text-slate-700">{formatTanggalID(r.awal_penugasan) || "—"}</div>
                <div className="text-[10px] text-slate-500">s.d. {formatTanggalID(r.akhir_penugasan) || "—"}</div>
              </TableCell>
              <TableCell>
                <StatusBadge status={r.status} />
                <PerluTindakanBadges items={r.perlu_tindakan} testId={`sppd-row-tindakan-${r.id}`} />
              </TableCell>
              {highlightPembayaran && (
                <TableCell>
                  {r.section4_status === "pending_admin" && <Badge className="bg-amber-100 text-amber-800 border border-amber-200 mr-1">UM pending</Badge>}
                  {r.section7_status === "pending_admin" && <Badge className="bg-amber-100 text-amber-800 border border-amber-200">PJ pending</Badge>}
                </TableCell>
              )}
              <TableCell className="text-right">
                <Button size="sm" variant="outline" className="gap-1" onClick={() => onOpen(r.id)} data-testid={`sppd-open-${r.id}`}>
                  Lihat <ChevronRight size={12} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ========================================================================
// CREATE DIALOG — form pengajuan
// ========================================================================

function CreateDialog({ open, onClose, onSuccess }) {
  const [pegawai, setPegawai] = useState([]);
  const [kualifikasi, setKualifikasi] = useState([]);
  const [settings, setSettings] = useState({ max_hari_per_perintah: 14 });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [form, setForm] = useState({
    nama_perusahaan: "", tempat_pelaksanaan: "", untuk_melaksanakan: "",
    awal_penugasan: "", akhir_penugasan: "", kualifikasi_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [distanceInfo, setDistanceInfo] = useState(null); // { distance_km, duration_text, kualifikasi_id, kualifikasi_nama }
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [place, setPlace] = useState(null); // { place_id, name, address, latitude, longitude, description }

  useEffect(() => {
    (async () => {
      try {
        const [p, k, s] = await Promise.all([
          api.get("/sppd/pegawai-dalam-unit"),
          api.get("/sppd/kualifikasi"),
          api.get("/sppd/settings"),
        ]);
        setPegawai(p.data || []);
        setKualifikasi(k.data || []);
        setSettings(s.data || {});
      } catch (e) { toast.error(apiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  async function calcDistance(placeArg) {
    const p = placeArg || place;
    const destination = (p?.description || form.tempat_pelaksanaan).trim();
    if (selected.size === 0) { toast.error("Pilih minimal 1 pegawai dulu untuk kalkulasi jarak"); return; }
    if (!destination) { toast.error("Isi Tempat Pelaksanaan dulu"); return; }
    const firstUid = Array.from(selected)[0];
    const params = { user_id: firstUid, destination };
    if (p) { params.dest_lat = p.latitude; params.dest_lng = p.longitude; }
    setDistanceLoading(true);
    try {
      const { data } = await api.get("/sppd/distance-matrix", { params });
      setDistanceInfo(data);
      // Auto-pilih kualifikasi dari backend bila belum di-override
      if (data.kualifikasi_id && !form.kualifikasi_id) {
        setForm((f) => ({ ...f, kualifikasi_id: data.kualifikasi_id }));
      }
      toast.success(`Jarak ${data.distance_km} km — ${data.kualifikasi_nama || "tidak ada kualifikasi cocok"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal menghitung jarak")); }
    finally { setDistanceLoading(false); }
  }

  function onPlaceSelected(p) {
    setForm((f) => ({ ...f, tempat_pelaksanaan: (p.description || p.address || p.name || "").slice(0, 200) }));
    setPlace(p);
    setDistanceInfo(null);
    if (selected.size > 0) calcDistance(p);
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pegawai;
    return pegawai.filter((p) =>
      (p.name || "").toLowerCase().includes(q) || (p.npp || "").toLowerCase().includes(q)
      || (p.jabatan || "").toLowerCase().includes(q),
    );
  }, [pegawai, search]);

  function toggle(id) {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  }

  async function submit() {
    if (selected.size === 0) { toast.error("Pilih minimal 1 pegawai pelaksana"); return; }
    if (!form.nama_perusahaan.trim() || !form.tempat_pelaksanaan.trim() || !form.untuk_melaksanakan.trim()) {
      toast.error("Nama Perusahaan, Tempat, dan Keperluan wajib diisi"); return;
    }
    if (!form.kualifikasi_id) { toast.error("Pilih Kualifikasi Jarak"); return; }
    setSaving(true);
    try {
      const payload = {
        pegawai_user_ids: Array.from(selected),
        nama_perusahaan: form.nama_perusahaan.trim(),
        tempat_pelaksanaan: form.tempat_pelaksanaan.trim(),
        untuk_melaksanakan: form.untuk_melaksanakan.trim(),
        awal_penugasan: form.awal_penugasan || null,
        akhir_penugasan: form.akhir_penugasan || null,
        kualifikasi_id: form.kualifikasi_id,
        tempat_place_id: place?.place_id || null,
        tempat_alamat: place?.address || null,
        tempat_lat: place?.latitude ?? null,
        tempat_lng: place?.longitude ?? null,
      };
      const { data } = await api.post("/sppd/perintah", payload);
      toast.success(`${data.count} perintah SPPD berhasil diajukan`);
      onSuccess();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="sppd-create-dialog">
        <DialogHeader>
          <DialogTitle>Ajukan Perjalanan Dinas (SPPD)</DialogTitle>
          <DialogDescription>
            Pilih pegawai pelaksana (1 dokumen SPPD per pegawai). Max {settings.max_hari_per_perintah || 14} hari per penugasan.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center text-slate-400 py-10">Memuat…</div>
        ) : (
          <div className="space-y-4">
            {/* Pegawai picker */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Pegawai Pelaksana *</Label>
              <div className="relative mt-1.5">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Cari nama / NPP / jabatan…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="sppd-create-search" />
              </div>
              <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 max-h-56 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="text-slate-400 text-sm text-center py-6">Tidak ada pegawai cocok.</div>
                ) : filtered.map((p) => (
                  <label key={p.id} className="flex items-center gap-3 p-2 border-b border-slate-100 hover:bg-white cursor-pointer" data-testid={`sppd-create-pegawai-${p.id}`}>
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} className="w-4 h-4" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                      <div className="text-[11px] text-slate-500">{p.npp} · {p.jabatan || "-"}</div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{selected.size} pegawai dipilih · {filtered.length}/{pegawai.length} tampil</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama Perusahaan Tujuan *</Label>
                <Input className="mt-1.5" value={form.nama_perusahaan} onChange={(e) => setForm({ ...form, nama_perusahaan: e.target.value })} data-testid="sppd-create-nama-perusahaan" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tempat Pelaksanaan *</Label>
                <PlaceAutocomplete
                  value={form.tempat_pelaksanaan}
                  place={place}
                  onTextChange={(v) => { setForm((f) => ({ ...f, tempat_pelaksanaan: v })); setPlace(null); setDistanceInfo(null); }}
                  onPlaceSelected={onPlaceSelected}
                  placeholder="Ketik nama tempat / alamat, lalu pilih dari saran"
                  testId="sppd-create-tempat"
                />
                <div className="flex items-center gap-2 mt-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={distanceLoading || !form.tempat_pelaksanaan.trim() || selected.size === 0}
                    onClick={() => calcDistance()}
                    className="h-7 text-[11px] gap-1 border-sky-300 text-sky-700 hover:bg-sky-50"
                    data-testid="sppd-create-calc-distance"
                  >
                    <MapPin size={11} /> {distanceLoading ? "Menghitung…" : "Hitung Jarak Otomatis"}
                  </Button>
                  {distanceInfo && (
                    <span className="text-[11px] text-slate-600" data-testid="sppd-create-distance-info">
                      <b>{distanceInfo.distance_km} km</b> · {distanceInfo.duration_text}
                      {distanceInfo.kualifikasi_nama ? <span className="text-emerald-700 ml-1">→ {distanceInfo.kualifikasi_nama}</span> : null}
                    </span>
                  )}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Untuk Melaksanakan (Keperluan) *</Label>
                <Textarea className="mt-1.5" value={form.untuk_melaksanakan} onChange={(e) => setForm({ ...form, untuk_melaksanakan: e.target.value })} rows={2} data-testid="sppd-create-keperluan" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Awal Penugasan</Label>
                <Input type="date" className="mt-1.5" value={form.awal_penugasan} onChange={(e) => setForm({ ...form, awal_penugasan: e.target.value })} data-testid="sppd-create-awal" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Akhir Penugasan</Label>
                <Input type="date" className="mt-1.5" value={form.akhir_penugasan} onChange={(e) => setForm({ ...form, akhir_penugasan: e.target.value })} data-testid="sppd-create-akhir" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Kualifikasi Jarak *</Label>
                <Select value={form.kualifikasi_id} onValueChange={(v) => setForm({ ...form, kualifikasi_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sppd-create-kualifikasi"><SelectValue placeholder="Pilih kualifikasi jarak…" /></SelectTrigger>
                  <SelectContent>
                    {kualifikasi.map((k) => (<SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sppd-create-cancel">Batal</Button>
          <Button onClick={submit} disabled={saving || loading} className="bg-sky-700 hover:bg-sky-800 gap-2" data-testid="sppd-create-submit">
            <Send size={14} /> {saving ? "Mengajukan…" : "Ajukan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========================================================================
// DETAIL DIALOG — menampilkan semua info + action buttons sesuai role
// ========================================================================

function DetailDialog({ perintahId, onClose, onChanged, user }) {
  const [data, setData] = useState(null);
  const [tarif, setTarif] = useState([]);
  const [sppdSettings, setSppdSettings] = useState({});
  const [actionOpen, setActionOpen] = useState(null); // "approve-pst"|"reject-pst"|"approve-s4"|"reject-s4"|"approve-s7"|"reject-s7"
  const [actionCatatan, setActionCatatan] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [sec4Rows, setSec4Rows] = useState({}); // komponen_id -> pengali
  const [sec4Saving, setSec4Saving] = useState(false);
  const [sec7Rows, setSec7Rows] = useState({});
  const [sec7Dates, setSec7Dates] = useState({ awal: "", akhir: "" });
  const [sec7Saving, setSec7Saving] = useState(false);
  // Section 6 — log perjalanan (tiba/kembali) diisi bersama Section 7
  const [sec6Tiba, setSec6Tiba] = useState([{ lokasi: "", tgl: "" }]);
  const [sec6Kembali, setSec6Kembali] = useState([{ lokasi: "", tgl: "" }]);
  // Section 8 — approval granular per pejabat × fase
  const [sec8Loading, setSec8Loading] = useState(false);

  async function load() {
    try {
      const [d, t, s] = await Promise.all([
        api.get(`/sppd/perintah/${perintahId}`),
        api.get(`/sppd/perintah/${perintahId}/tarif-tersedia`),
        api.get(`/sppd/settings`).catch(() => ({ data: {} })),
      ]);
      setData(d.data);
      setTarif(t.data || []);
      setSppdSettings(s.data || {});
      const s4Map = {};
      (d.data.section4 || []).forEach((r) => { s4Map[r.komponen_id] = r.pengali; });
      setSec4Rows(s4Map);
      const s7Map = {};
      (d.data.section7 || []).forEach((r) => { s7Map[r.komponen_id] = r.pengali; });
      setSec7Rows(s7Map);
      // Section 6 — rehydrate
      const tibaRows = (d.data.section6 || []).filter((r) => r.tipe === "tiba").map((r) => ({ lokasi: r.lokasi || "", tgl: r.tgl || "" }));
      const kembaliRows = (d.data.section6 || []).filter((r) => r.tipe === "kembali").map((r) => ({ lokasi: r.lokasi || "", tgl: r.tgl || "" }));
      setSec6Tiba(tibaRows.length ? tibaRows : [{ lokasi: "", tgl: "" }]);
      setSec6Kembali(kembaliRows.length ? kembaliRows : [{ lokasi: "", tgl: "" }]);
      setSec7Dates({
        awal: d.data.awal_penugasan_aktual || d.data.awal_penugasan || "",
        akhir: d.data.akhir_penugasan_aktual || d.data.akhir_penugasan || "",
      });
    } catch (e) { toast.error(apiErrorMessage(e)); onClose(); }
  }
  useEffect(() => { if (perintahId) load(); /* eslint-disable-next-line */ }, [perintahId]);

  if (!data) return null;

  const uid = user?.id;
  const isPelaksana = data.pegawai_user_id === uid;
  const isPenandatangan = data.penandatangan_st_user_id === uid || user?.role === "admin";
  const isAdmin = user?.role === "admin";

  async function doAction(kind) {
    if ((kind === "reject-pst" || kind === "reject-s4" || kind === "reject-s7" || kind === "reject-chain") && !actionCatatan.trim()) {
      toast.error("Alasan wajib diisi"); return;
    }
    setActionLoading(true);
    const endpointMap = {
      "approve-pst": "/approve-penandatangan",
      "reject-pst": "/reject-penandatangan",
      "approve-s4": "/section4/approve",
      "reject-s4": "/section4/reject",
      "approve-s7": "/section7/approve",
      "reject-s7": "/section7/reject",
      "approve-chain": "/chain/approve",
      "reject-chain": "/chain/reject",
    };
    try {
      await api.post(`/sppd/perintah/${perintahId}${endpointMap[kind]}`, { catatan: actionCatatan });
      toast.success("Berhasil diproses");
      setActionOpen(null); setActionCatatan("");
      await load();
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setActionLoading(false); }
  }

  async function approveSec8(signer, phase) {
    setSec8Loading(true);
    try {
      await api.post(`/sppd/perintah/${perintahId}/section8/approve`, { signer, phase });
      toast.success(`TTD ${signer.replace('_', ' ')} (${phase}) berhasil disimpan`);
      await load();
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSec8Loading(false); }
  }

  async function saveSection4(submit) {
    setSec4Saving(true);
    try {
      const rows = Object.entries(sec4Rows)
        .filter(([_, v]) => Number(v) > 0)
        .map(([komponen_id, pengali]) => ({ komponen_id, pengali: Number(pengali) }));
      await api.put(`/sppd/perintah/${perintahId}/section4`, { rows, submit });
      toast.success(submit ? "Section 4 diajukan untuk approval admin" : "Section 4 disimpan sebagai draft");
      await load();
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSec4Saving(false); }
  }

  async function saveSection7(submit) {
    setSec7Saving(true);
    try {
      const rows = Object.entries(sec7Rows)
        .filter(([_, v]) => Number(v) > 0)
        .map(([komponen_id, pengali]) => ({ komponen_id, pengali: Number(pengali) }));
      // Section 6 — hanya kirim baris yg lokasi+tgl terisi (filter max 4 tiap tipe sudah dibatasi di UI).
      const section6_rows = [];
      sec6Tiba.slice(0, 4).forEach((r) => {
        if ((r.lokasi || "").trim() && r.tgl) section6_rows.push({ tipe: "tiba", lokasi: r.lokasi.trim(), tgl: r.tgl });
      });
      sec6Kembali.slice(0, 4).forEach((r) => {
        if ((r.lokasi || "").trim() && r.tgl) section6_rows.push({ tipe: "kembali", lokasi: r.lokasi.trim(), tgl: r.tgl });
      });
      await api.put(`/sppd/perintah/${perintahId}/section7`, {
        rows, submit,
        awal_penugasan_aktual: sec7Dates.awal || null,
        akhir_penugasan_aktual: sec7Dates.akhir || null,
        section6_rows,
      });
      toast.success(submit ? "Section 6 & 7 diajukan untuk approval admin" : "Section 6 & 7 disimpan sebagai draft");
      await load();
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSec7Saving(false); }
  }

  function downloadPdf(kind) {
    api.get(`/sppd/perintah/${perintahId}/${kind === "st" ? "surat-tugas.pdf" : "sppd.pdf"}`, { responseType: "blob" })
      .then((r) => {
        const u = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = u;
        a.download = (kind === "st" ? "SuratTugas-" : "SPPD-") + (data.nomor_st || perintahId).replace(/\//g, "-") + ".pdf";
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch((e) => toast.error(apiErrorMessage(e, "Gagal unduh PDF")));
  }

  // Hitung total realtime
  function sumRows(rowMap) {
    let total = 0;
    tarif.forEach((t) => {
      const p = Number(rowMap[t.komponen_id] || 0);
      total += p * Number(t.tarif || 0);
    });
    return total;
  }

  const s4total = sumRows(sec4Rows);
  const s7total = sumRows(sec7Rows);
  const canEditS4 = isPelaksana && data.status === "Surat Tugas Terbit" && !["pending_admin", "approved"].includes(data.section4_status);
  const canEditS7 = isPelaksana && data.section4_status === "approved" && !["pending_admin", "approved"].includes(data.section7_status);
  const todayAfterAkhir = (() => {
    const akhir = sec7Dates.akhir || data.akhir_penugasan;
    if (!akhir) return true;
    return new Date().toISOString().slice(0, 10) > akhir;
  })();

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="sppd-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane size={16} className="text-sky-700" /> Detail SPPD
          </DialogTitle>
          <DialogDescription>{data.nomor_st || "Belum terbit"}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status & nomor */}
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <StatusBadge status={data.status} />
            {data.nomor_st && <Badge variant="outline">ST: <span className="font-mono-clock ml-1">{data.nomor_st}</span></Badge>}
            {data.nomor_sppd && <Badge variant="outline">SPPD: <span className="font-mono-clock ml-1">{data.nomor_sppd}</span></Badge>}
          </div>

          {data.perlu_tindakan?.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 flex items-start gap-2" data-testid="sppd-detail-tindakan-banner">
              <BellRing size={16} className="shrink-0 mt-0.5" />
              <div>
                <b>Perlu Tindakan Anda:</b> {data.perlu_tindakan.join(" · ")}
              </div>
            </div>
          )}

          {/* Info utama */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg border border-slate-200 p-4">
            <KV label="Pengaju" value={data.pengaju_nama} />
            <KV label="Pelaksana" value={`${data.pegawai_nama} (${data.pegawai_npp})`} />
            <KV label="Jabatan" value={data.pegawai_jabatan_nama} />
            <KV label="Nama Perusahaan Tujuan" value={data.nama_perusahaan} />
            <KV label="Tempat" value={data.tempat_pelaksanaan} />
            {data.tempat_lat != null && data.tempat_lng != null && (
              <KV
                label="Titik Lokasi Tujuan"
                value={(
                  <a href={`https://www.google.com/maps/search/?api=1&query=${data.tempat_lat},${data.tempat_lng}`} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline inline-flex items-center gap-1" data-testid="sppd-detail-tempat-map-link">
                    <MapPin size={12} /> {Number(data.tempat_lat).toFixed(5)}, {Number(data.tempat_lng).toFixed(5)}
                  </a>
                )}
              />
            )}
            <KV label="Kualifikasi" value={data.kualifikasi_nama} />
            <KV label="Awal Penugasan" value={formatTanggalID(data.awal_penugasan)} />
            <KV label="Akhir Penugasan" value={formatTanggalID(data.akhir_penugasan)} />
            <div className="md:col-span-2"><KV label="Keperluan" value={data.untuk_melaksanakan} /></div>
          </div>

          {/* Approval Chain (Atasan L1/L2/L3) — hanya muncul jika doc punya chain */}
          {Array.isArray(data.approval_chain) && data.approval_chain.length > 0 && (
            <div className="rounded-lg border border-slate-200 p-4" data-testid="sppd-approval-chain">
              <div className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <Check size={14} className="text-sky-700" /> Alur Approval Atasan
              </div>
              <ol className="space-y-2">
                {data.approval_chain.map((e) => {
                  const statusColor = e.status === "Disetujui"
                    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                    : e.status === "Ditolak"
                    ? "bg-rose-100 text-rose-800 border-rose-200"
                    : e.status === "Menunggu" && data.current_approver_id === e.approver_id
                    ? "bg-amber-100 text-amber-800 border-amber-300"
                    : "bg-slate-100 text-slate-700 border-slate-200";
                  return (
                    <li key={e.level} className="flex items-start gap-3 text-sm" data-testid={`sppd-chain-level-${e.level}`}>
                      <span className="shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center">{e.level}</span>
                      <div className="flex-1">
                        <div className="font-semibold text-slate-800">{e.role_label}</div>
                        <div className="text-xs text-slate-600">{e.approver_nama} · {(e.approver_role || "").replace(/_/g, " ")}</div>
                        {e.catatan && <div className="text-xs text-slate-500 italic mt-0.5">"{e.catatan}"</div>}
                      </div>
                      <Badge className={`${statusColor} border`}>{e.status}</Badge>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Action: Chain Atasan (L2/L3) */}
          {data.status === "Menunggu Approval Atasan" && data.current_approver_id === uid && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="sppd-action-chain">
              <div className="font-semibold text-amber-900 mb-2 flex items-center gap-2"><Check size={14} /> Perlu Tindakan Anda — Approve Pengajuan SPPD</div>
              <p className="text-xs text-amber-800 mb-2">Sebagai atasan dalam alur approval, mohon setujui atau tolak pengajuan ini.</p>
              <div className="flex gap-2">
                <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={() => { setActionOpen("approve-chain"); setActionCatatan(""); }} data-testid="sppd-approve-chain-btn"><Check size={14} /> Setujui</Button>
                <Button variant="outline" className="text-rose-700 border-rose-300 gap-2" onClick={() => { setActionOpen("reject-chain"); setActionCatatan(""); }} data-testid="sppd-reject-chain-btn"><X size={14} /> Tolak</Button>
              </div>
            </div>
          )}

          {/* Action: Penandatangan ST */}
          {isPenandatangan && data.penandatangan_action === "pending" && data.status === "Menunggu Approval Penandatangan ST" && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4" data-testid="sppd-action-pst">
              <div className="font-semibold text-indigo-900 mb-2 flex items-center gap-2"><FileCheck size={14} /> Perlu Tindakan Anda — Setujui &amp; Terbitkan Surat Tugas</div>
              <p className="text-xs text-indigo-800 mb-2">Nomor ST &amp; SPPD akan di-generate otomatis saat Anda menyetujui.</p>
              <div className="flex gap-2">
                <Button className="bg-indigo-700 hover:bg-indigo-800 gap-2" onClick={() => { setActionOpen("approve-pst"); setActionCatatan(""); }} data-testid="sppd-approve-pst-btn"><Check size={14} /> Setujui &amp; Terbitkan</Button>
                <Button variant="outline" className="text-rose-700 border-rose-300 gap-2" onClick={() => { setActionOpen("reject-pst"); setActionCatatan(""); }} data-testid="sppd-reject-pst-btn"><X size={14} /> Tolak</Button>
              </div>
            </div>
          )}

          {/* Section 4 — Uang Muka */}
          {data.status === "Surat Tugas Terbit" && (
            <div className="rounded-lg border border-slate-200 p-4" data-testid="sppd-section4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold text-slate-800 flex items-center gap-2"><Wallet size={14} className="text-emerald-700" /> Section 4 — Perhitungan Uang Muka</div>
                <Badge className={data.section4_status === "approved" ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : data.section4_status === "pending_admin" ? "bg-amber-100 text-amber-800 border border-amber-200" : data.section4_status === "rejected" ? "bg-rose-100 text-rose-800 border border-rose-200" : "bg-slate-100 text-slate-700 border border-slate-200"}>
                  {data.section4_status === "approved" ? "Disetujui" : data.section4_status === "pending_admin" ? "Menunggu Approval Admin" : data.section4_status === "rejected" ? "Ditolak — perlu revisi" : "Draft"}
                </Badge>
              </div>
              {canEditS4 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px] font-bold uppercase">Komponen</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase w-24 text-center">Hari</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase text-right">Tarif</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase text-right">Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tarif.map((t) => (
                        <TableRow key={t.komponen_id}>
                          <TableCell>{t.komponen_nama}</TableCell>
                          <TableCell className="text-center">
                            <Select value={String(sec4Rows[t.komponen_id] || "0")} onValueChange={(v) => setSec4Rows({ ...sec4Rows, [t.komponen_id]: Number(v) })}>
                              <SelectTrigger className="h-8 w-20 mx-auto" data-testid={`sppd-s4-pengali-${t.komponen_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 32 }, (_, i) => i).map((n) => (
                                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right text-slate-600">{rp(t.tarif)}</TableCell>
                          <TableCell className="text-right font-semibold">{rp(Number(sec4Rows[t.komponen_id] || 0) * Number(t.tarif || 0))}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={3} className="text-right font-bold">TOTAL</TableCell>
                        <TableCell className="text-right font-bold text-lg">{rp(s4total)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button variant="outline" onClick={() => saveSection4(false)} disabled={sec4Saving} data-testid="sppd-s4-save-draft">Simpan Draft</Button>
                    <Button className="bg-emerald-700 hover:bg-emerald-800 gap-2" onClick={() => saveSection4(true)} disabled={sec4Saving} data-testid="sppd-s4-submit"><Send size={14} /> Ajukan ke Admin</Button>
                  </div>
                </>
              ) : (
                <>
                  <RenderRowsReadonly rows={data.section4} total={data.section4_total} terbilang={data.section4_terbilang} />
                  {data.section4_catatan && <div className="text-xs text-slate-500 mt-2"><b>Catatan Admin:</b> {data.section4_catatan}</div>}
                  {isAdmin && data.section4_status === "pending_admin" && (
                    <div className="flex gap-2 mt-3">
                      <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={() => { setActionOpen("approve-s4"); setActionCatatan(""); }} data-testid="sppd-s4-approve"><Check size={14} /> Setujui Uang Muka</Button>
                      <Button variant="outline" className="text-rose-700 border-rose-300 gap-2" onClick={() => { setActionOpen("reject-s4"); setActionCatatan(""); }} data-testid="sppd-s4-reject"><X size={14} /> Tolak </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Section 7 — Pertanggungjawaban */}
          {data.section4_status === "approved" && (
            <div className="rounded-lg border border-slate-200 p-4" data-testid="sppd-section7">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold text-slate-800 flex items-center gap-2"><Receipt size={14} className="text-teal-700" /> Section 7 — Pertanggungjawaban</div>
                <Badge className={data.section7_status === "approved" ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : data.section7_status === "pending_admin" ? "bg-amber-100 text-amber-800 border border-amber-200" : data.section7_status === "rejected" ? "bg-rose-100 text-rose-800 border border-rose-200" : "bg-slate-100 text-slate-700 border border-slate-200"}>
                  {data.section7_status === "approved" ? "Disetujui" : data.section7_status === "pending_admin" ? "Menunggu Approval Admin" : data.section7_status === "rejected" ? "Ditolak — perlu revisi" : "Draft"}
                </Badge>
              </div>
              {canEditS7 ? (
                todayAfterAkhir ? (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Awal Aktual</Label>
                      <Input type="date" className="mt-1" value={sec7Dates.awal} onChange={(e) => setSec7Dates({ ...sec7Dates, awal: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Akhir Aktual</Label>
                      <Input type="date" className="mt-1" value={sec7Dates.akhir} onChange={(e) => setSec7Dates({ ...sec7Dates, akhir: e.target.value })} />
                    </div>
                  </div>

                  {/* Section 6 — Log Perjalanan (Tiba/Kembali) */}
                  <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 mb-3" data-testid="sppd-section6-edit">
                    <div className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2">
                      <MapPin size={14} className="text-sky-700" /> Section 6 — Pelaksana Perjalanan Dinas
                    </div>
                    <p className="text-[11px] text-slate-500 mb-2">Isi hingga 4 baris "Tiba di" &amp; 4 baris "Kembali dari". Baris kosong tidak akan muncul di PDF.</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Tiba di</Label>
                        <div className="space-y-1.5 mt-1.5">
                          {sec6Tiba.map((row, idx) => (
                            <div key={`tiba-${idx}`} className="grid grid-cols-[1fr_auto] gap-1.5">
                              <Input placeholder={`Lokasi tiba #${idx + 1}`} value={row.lokasi} onChange={(e) => {
                                const n = [...sec6Tiba]; n[idx] = { ...n[idx], lokasi: e.target.value }; setSec6Tiba(n);
                              }} className="h-8 text-sm" data-testid={`sppd-s6-tiba-lokasi-${idx}`} />
                              <Input type="date" value={row.tgl} onChange={(e) => {
                                const n = [...sec6Tiba]; n[idx] = { ...n[idx], tgl: e.target.value }; setSec6Tiba(n);
                              }} className="h-8 w-40 text-sm" data-testid={`sppd-s6-tiba-tgl-${idx}`} />
                            </div>
                          ))}
                          {sec6Tiba.length < 4 && (
                            <Button type="button" size="sm" variant="outline" onClick={() => setSec6Tiba([...sec6Tiba, { lokasi: "", tgl: "" }])} className="h-7 text-xs" data-testid="sppd-s6-tiba-add">+ Tambah baris</Button>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Kembali dari</Label>
                        <div className="space-y-1.5 mt-1.5">
                          {sec6Kembali.map((row, idx) => (
                            <div key={`kembali-${idx}`} className="grid grid-cols-[1fr_auto] gap-1.5">
                              <Input placeholder={`Lokasi kembali #${idx + 1}`} value={row.lokasi} onChange={(e) => {
                                const n = [...sec6Kembali]; n[idx] = { ...n[idx], lokasi: e.target.value }; setSec6Kembali(n);
                              }} className="h-8 text-sm" data-testid={`sppd-s6-kembali-lokasi-${idx}`} />
                              <Input type="date" value={row.tgl} onChange={(e) => {
                                const n = [...sec6Kembali]; n[idx] = { ...n[idx], tgl: e.target.value }; setSec6Kembali(n);
                              }} className="h-8 w-40 text-sm" data-testid={`sppd-s6-kembali-tgl-${idx}`} />
                            </div>
                          ))}
                          {sec6Kembali.length < 4 && (
                            <Button type="button" size="sm" variant="outline" onClick={() => setSec6Kembali([...sec6Kembali, { lokasi: "", tgl: "" }])} className="h-7 text-xs" data-testid="sppd-s6-kembali-add">+ Tambah baris</Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px] font-bold uppercase">Komponen</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase w-24 text-center">Hari</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase text-right">Tarif</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase text-right">Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tarif.map((t) => (
                        <TableRow key={t.komponen_id}>
                          <TableCell>{t.komponen_nama}</TableCell>
                          <TableCell className="text-center">
                            <Select value={String(sec7Rows[t.komponen_id] || "0")} onValueChange={(v) => setSec7Rows({ ...sec7Rows, [t.komponen_id]: Number(v) })}>
                              <SelectTrigger className="h-8 w-20 mx-auto"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 32 }, (_, i) => i).map((n) => (
                                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right text-slate-600">{rp(t.tarif)}</TableCell>
                          <TableCell className="text-right font-semibold">{rp(Number(sec7Rows[t.komponen_id] || 0) * Number(t.tarif || 0))}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={3} className="text-right font-bold">TOTAL REALISASI</TableCell>
                        <TableCell className="text-right font-bold text-lg">{rp(s7total)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={3} className="text-right text-slate-600">Uang Muka Diterima</TableCell>
                        <TableCell className="text-right">{rp(data.section4_total)}</TableCell>
                      </TableRow>
                      <TableRow className="bg-amber-50">
                        <TableCell colSpan={3} className="text-right font-bold">Dikembalikan / Ditagihkan</TableCell>
                        <TableCell className="text-right font-bold">{rp(s7total - (data.section4_total || 0))}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button variant="outline" onClick={() => saveSection7(false)} disabled={sec7Saving}>Simpan Draft</Button>
                    <Button className="bg-teal-700 hover:bg-teal-800 gap-2" onClick={() => saveSection7(true)} disabled={sec7Saving} data-testid="sppd-s7-submit"><Send size={14} /> Ajukan ke Admin</Button>
                  </div>
                </>
                ) : (
                  <div className="text-slate-500 text-sm py-4">Section 7 dapat diisi setelah tanggal akhir penugasan ({formatTanggalID(data.akhir_penugasan)}) terlampaui.</div>
                )
              ) : (
                <>
                  {/* Section 6 readonly tampilan */}
                  {data.section6 && data.section6.length > 0 && (
                    <div className="mb-3 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="sppd-section6-view">
                      <div className="font-bold text-slate-800 text-sm mb-1.5 flex items-center gap-2">
                        <MapPin size={13} className="text-sky-700" /> Section 6 — Log Perjalanan
                      </div>
                      <div className="grid md:grid-cols-2 gap-2 text-xs">
                        {data.section6.filter((r) => r.tipe === "tiba").map((r, i) => (
                          <div key={`t-${i}`} className="flex justify-between gap-2"><span className="text-slate-500">Tiba di</span><span className="font-medium">{r.lokasi} <span className="text-slate-400">({formatTanggalID(r.tgl)})</span></span></div>
                        ))}
                        {data.section6.filter((r) => r.tipe === "kembali").map((r, i) => (
                          <div key={`k-${i}`} className="flex justify-between gap-2"><span className="text-slate-500">Kembali dari</span><span className="font-medium">{r.lokasi} <span className="text-slate-400">({formatTanggalID(r.tgl)})</span></span></div>
                        ))}
                      </div>
                    </div>
                  )}
                  {data.section7 && data.section7.length > 0 ? (
                    <>
                      <RenderRowsReadonly rows={data.section7} total={data.section7_total} terbilang={data.section7_terbilang} />
                      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mt-3 text-sm text-amber-900">
                        <div>Uang Muka: <b>{rp(data.section4_total)}</b></div>
                        <div>Realisasi: <b>{rp(data.section7_total)}</b></div>
                        <div>Selisih: <b>{rp((data.section7_total || 0) - (data.section4_total || 0))}</b> {(data.section7_total || 0) > (data.section4_total || 0) ? "(ditagihkan tambahan)" : (data.section7_total || 0) < (data.section4_total || 0) ? "(dikembalikan)" : "(seimbang)"}</div>
                      </div>
                    </>
                  ) : (
                    <div className="text-slate-500 text-sm py-4">Pertanggungjawaban belum diisi oleh pelaksana.</div>
                  )}
                  {data.section7_catatan && <div className="text-xs text-slate-500 mt-2"><b>Catatan Admin:</b> {data.section7_catatan}</div>}
                  {isAdmin && data.section7_status === "pending_admin" && (
                    <div className="flex gap-2 mt-3">
                      <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={() => { setActionOpen("approve-s7"); setActionCatatan(""); }} data-testid="sppd-s7-approve"><Check size={14} /> Setujui Pertanggungjawaban</Button>
                      <Button variant="outline" className="text-rose-700 border-rose-300 gap-2" onClick={() => { setActionOpen("reject-s7"); setActionCatatan(""); }} data-testid="sppd-s7-reject"><X size={14} /> Tolak </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Section 8 — Persetujuan dan Tanda Tangan */}
          {data.status === "Surat Tugas Terbit" && (
            <Section8Panel data={data} settings={sppdSettings} onApprove={approveSec8} loading={sec8Loading} />
          )}

          {/* PDF download */}
          {data.status === "Surat Tugas Terbit" && (
            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="outline" className="gap-2" onClick={() => downloadPdf("st")} data-testid="sppd-download-st"><Download size={14} /> Unduh Surat Tugas</Button>
              <Button variant="outline" className="gap-2" onClick={() => downloadPdf("sppd")} data-testid="sppd-download-sppd"><Download size={14} /> Unduh SPPD</Button>
            </div>
          )}

          {/* Timeline sederhana */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            <div className="font-bold text-slate-800 mb-2">Riwayat</div>
            <div>• Dibuat: {formatTanggalID(data.created_at?.slice(0, 10))} oleh {data.pengaju_nama}</div>
            {data.pegawai_action !== "pending" && (<div>• Pelaksana {data.pegawai_action === "approved" ? "menerima" : "menolak"}: {formatTanggalID(data.pegawai_action_at?.slice(0, 10))}{data.pegawai_action_catatan ? ` — "${data.pegawai_action_catatan}"` : ""}</div>)}
            {data.penandatangan_action !== "pending" && (<div>• Penandatangan ST {data.penandatangan_action === "approved" ? "menyetujui" : "menolak"}: {formatTanggalID(data.penandatangan_action_at?.slice(0, 10))}{data.penandatangan_action_catatan ? ` — "${data.penandatangan_action_catatan}"` : ""}</div>)}
            {data.section4_approved_at && (<div>• Uang Muka disetujui: {formatTanggalID(data.section4_approved_at?.slice(0, 10))}</div>)}
            {data.section7_approved_at && (<div>• Pertanggungjawaban disetujui: {formatTanggalID(data.section7_approved_at?.slice(0, 10))}</div>)}
          </div>
        </div>

        {/* Catatan modal untuk approve/reject */}
        {actionOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="sppd-action-modal">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
              <div className="font-bold text-slate-900 mb-2">
                {actionOpen.startsWith("approve") ? "Konfirmasi Persetujuan" : "Konfirmasi Penolakan"}
              </div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                {actionOpen.startsWith("approve") ? "Catatan (opsional)" : "Alasan Penolakan *"}
              </Label>
              <Textarea className="mt-1.5" value={actionCatatan} onChange={(e) => setActionCatatan(e.target.value)} rows={3} data-testid="sppd-action-catatan" />
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setActionOpen(null)} disabled={actionLoading}>Batal</Button>
                <Button
                  className={actionOpen.startsWith("approve") ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
                  onClick={() => doAction(actionOpen)}
                  disabled={actionLoading}
                  data-testid="sppd-action-confirm"
                >
                  {actionLoading ? "Memproses…" : "Konfirmasi"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 font-medium mt-0.5">{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

// ========================================================================
// SECTION 8 — Panel Persetujuan & TTD (per pejabat × fase awal/akhir)
// ========================================================================

function Section8Panel({ data, settings, onApprove, loading }) {
  const sec7Approved = data.section7_status === "approved";
  const approvals = data.section8_approvals || {};
  const myRoles = new Set(data.my_signer_roles || []);
  const cfg = settings || {};

  function approvalCell(signer, phase) {
    const info = (approvals[signer] || {})[phase];
    const tid = `sppd-s8-status-${signer}-${phase}`;
    if (phase === "akhir" && !sec7Approved) {
      return <div className="text-[11px] italic text-slate-400" data-testid={tid}>Menunggu pertanggungjawaban</div>;
    }
    if (info?.approved_at) {
      return (
        <div className="text-[11px] space-y-0.5" data-testid={tid}>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 font-semibold">
            <Check size={11} /> Sudah TTD
          </span>
          <div className="text-slate-600">{formatTanggalID((info.approved_at || "").slice(0, 10))}</div>
          {info.by_name && <div className="text-slate-400">oleh {info.by_name}</div>}
        </div>
      );
    }
    if (myRoles.has(signer)) {
      return (
        <Button
          size="sm"
          disabled={loading}
          className="h-8 text-[11px] gap-1 bg-indigo-700 hover:bg-indigo-800 text-white"
          onClick={() => onApprove(signer, phase)}
          data-testid={`sppd-s8-approve-${signer}-${phase}`}
        >
          <PenLine size={12} /> Tanda Tangani
        </Button>
      );
    }
    return (
      <div className="inline-flex items-center gap-1 text-[11px] italic text-slate-400" data-testid={tid}>
        <Clock size={11} /> Belum TTD
      </div>
    );
  }

  const nameMap = {
    pelaksana: data.pegawai_nama || "—",
    manager: data.manager_unit_nama || "—",
    kadiv_keu: cfg.kadiv_keu_nama || "—",
    direktur: cfg.direktur_nama || "—",
    direktur_utama: cfg.direktur_utama_nama || "—",
  };
  const rows = SECTION8_SIGNERS.map((s) => ({ ...s, nama: nameMap[s.key] }));

  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid="sppd-section8">
      <div className="font-bold text-slate-800 flex items-center gap-2 mb-3">
        <ClipboardList size={14} className="text-indigo-700" /> Section 8 — Persetujuan dan Tanda Tangan
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        Status TTD sama untuk semua pengguna. Tombol <b>Tanda Tangani</b> hanya muncul di slot milik Anda; QR tanda tangan tercetak di PDF setelah slot berstatus <b>Sudah TTD</b>. Kolom <b>AKHIR PENUGASAN</b> baru bisa ditandatangani setelah pertanggungjawaban (Section 7) disetujui.
      </p>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-[10px] font-bold uppercase">Peran</TableHead>
            <TableHead className="text-[10px] font-bold uppercase">Awal Penugasan</TableHead>
            <TableHead className="text-[10px] font-bold uppercase">Nama</TableHead>
            <TableHead className="text-[10px] font-bold uppercase">Akhir Penugasan</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.key}>
              <TableCell className="text-xs font-semibold text-slate-700">{`8.${i + 1} ${r.label}`}</TableCell>
              <TableCell>{approvalCell(r.key, "awal")}</TableCell>
              <TableCell className="text-xs font-semibold text-slate-900">{r.nama}</TableCell>
              <TableCell>{approvalCell(r.key, "akhir")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}



function RenderRowsReadonly({ rows, total, terbilang }) {
  if (!rows || rows.length === 0) return <div className="text-slate-400 text-sm py-2">Belum ada baris.</div>;
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-[10px] font-bold uppercase">Komponen</TableHead>
            <TableHead className="text-[10px] font-bold uppercase w-20 text-center">Hari</TableHead>
            <TableHead className="text-[10px] font-bold uppercase text-right">Tarif</TableHead>
            <TableHead className="text-[10px] font-bold uppercase text-right">Subtotal</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.komponen_nama}</TableCell>
              <TableCell className="text-center">{r.pengali}</TableCell>
              <TableCell className="text-right text-slate-600">{rp(r.nilai_tarif)}</TableCell>
              <TableCell className="text-right font-semibold">{rp(r.total)}</TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-slate-50">
            <TableCell colSpan={3} className="text-right font-bold">TOTAL</TableCell>
            <TableCell className="text-right font-bold text-lg">{rp(total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {terbilang && <div className="text-xs italic text-slate-500 mt-2">Terbilang: {terbilang}</div>}
    </>
  );
}
